// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — A7 CensusStrip (Item 9). docs/ux/SHELL-COMPOSITION.md §2.7.
//
// THE HONEST DENOMINATOR. A thin, calm, ALWAYS-VISIBLE pill docked along the bottom
// edge (above the Throughline rung) that names what is released to the operator right
// now as the RELEASED · REDUCED · REQUESTABLE triad (the design canon, Tak-TDF COP.dc.html
// line 482):
//
//     [ N RELEASED · N REDUCED · (cond) N REQUESTABLE ]   {caption}
//
//   - RELEASED    = full-fidelity visible objects (semantic GRANT green).
//   - REDUCED     = visible-but-degraded/approximate objects (semantic CAUTION amber). A
//                   reduced object is STILL VISIBLE — this is a fidelity fade, NOT a denial.
//   - REQUESTABLE = a FORWARD-LOOKING placeholder (0 today). The span renders ONLY when
//                   requestable>0 (so it is hidden today, but the slot exists). It NEVER
//                   reconstructs a hidden universe — there is no deny/request PDP path wired.
//
// GLOMAR (rule 3): every count here is computed by the host over the CLEARED-VISIBLE set
// only (status.census). A withheld object contributes NOTHING — it is byte-and-timing
// identical to a non-existent one. We NEVER render the word "withheld" or a hidden tail.
//
// The mock pill NEVER hides (it is a flat always-visible pill, not a collapsible toggle),
// so there is no expand/collapse state and no Esc-stack registration here.
//
// React stays OUT of the render loop: this component only reads the immutable status
// snapshot. No host, no MapLibre, no engine imports.

import type { CensusCounts } from "../host/shellContract.ts";
import "./census.css";

/** The census slice this component reads. Structurally satisfied by `CopStatus`,
 *  so the orchestrator can pass the whole `status` object as the prop. */
interface CensusStripProps {
  status: { census: CensusCounts };
}

export function CensusStrip({ status }: CensusStripProps): JSX.Element {
  const { released, reduced, requestable } = status.census;

  // The caption switches on whether anything is requestable (mock line 990). Both strings
  // are Glomar-clean: neither implies a hidden universe.
  const caption =
    requestable > 0
      ? "Reduced tracks are released at lower fidelity. Requestable datasets unlock through an access request."
      : "You see only what is released to you. Nothing here is hidden in plain sight.";

  return (
    <div className="census" role="status" aria-label="Released, reduced, and requestable picture">
      <div className="census__pill">
        <span className="census__num census__num--released">{released}</span>
        <span className="census__label">RELEASED</span>

        <span className="census__sep" aria-hidden="true">
          ·
        </span>

        <span className="census__num census__num--reduced">{reduced}</span>
        <span className="census__label">REDUCED</span>

        {requestable > 0 && (
          <>
            <span className="census__sep" aria-hidden="true">
              ·
            </span>
            <span className="census__num census__num--requestable">{requestable}</span>
            <span className="census__label">REQUESTABLE</span>
          </>
        )}
      </div>

      <div className="census__caption">{caption}</div>
    </div>
  );
}
